import React from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  TimePicker,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, FieldTimeOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import {
  ScheduledInput,
  ScheduledNotification,
  ScheduledRepeatFreq,
  useCancelScheduled,
  useCreateScheduled,
  useScheduledNotifications,
  useSendScheduledNow,
  useUpdateScheduled,
} from '@shared/api/hooks/useNotifications';

type ScheduleKind = 'ONESHOT' | 'DAILY' | 'WEEKLY';

interface FormValues {
  titleIt: string;
  titleEn?: string;
  bodyIt: string;
  bodyEn?: string;
  categoryFilter?: 'matches' | 'shop' | 'receipts' | 'posts';
  kind: ScheduleKind;
  when?: Dayjs; // una-tantum
  timeOfDay?: Dayjs; // periodica
  weekdays?: number[]; // WEEKLY (ISO 1..7)
}

// Giorni in ordine ISO (1=lun … 7=dom): il valore è già l'ISO weekday del backend.
const WEEKDAYS: Array<{ label: string; value: number }> = [
  { label: 'Lun', value: 1 },
  { label: 'Mar', value: 2 },
  { label: 'Mer', value: 3 },
  { label: 'Gio', value: 4 },
  { label: 'Ven', value: 5 },
  { label: 'Sab', value: 6 },
  { label: 'Dom', value: 7 },
];

const STATUS_TAG: Record<ScheduledNotification['status'], { color: string; label: string }> = {
  SCHEDULED: { color: 'blue', label: 'Programmata' },
  SENDING: { color: 'gold', label: 'In invio' },
  SENT: { color: 'green', label: 'Inviata' },
  CANCELLED: { color: 'default', label: 'Annullata' },
  FAILED: { color: 'red', label: 'Errore' },
};

function pickIt(v: { it?: string; en?: string } | undefined): string {
  return v?.it ?? v?.en ?? '';
}

/** Traduce i valori del form nel corpo API. Ora locale scelta → wall-clock, il backend la interpreta in Europe/Rome. */
function toInput(v: FormValues): ScheduledInput {
  const base: ScheduledInput = {
    title: { it: v.titleIt, ...(v.titleEn ? { en: v.titleEn } : {}) },
    body: { it: v.bodyIt, ...(v.bodyEn ? { en: v.bodyEn } : {}) },
    ...(v.categoryFilter ? { categoryFilter: v.categoryFilter } : {}),
  };
  if (v.kind === 'ONESHOT') {
    return { ...base, scheduledAtLocal: v.when ? v.when.format('YYYY-MM-DDTHH:mm') : undefined };
  }
  const freq: ScheduledRepeatFreq = v.kind;
  return {
    ...base,
    repeatFreq: freq,
    localTime: v.timeOfDay ? v.timeOfDay.format('HH:mm') : undefined,
    ...(freq === 'WEEKLY' ? { repeatDays: v.weekdays ?? [] } : {}),
  };
}

function describeSchedule(n: ScheduledNotification): string {
  if (n.repeatFreq === 'DAILY') return `Ogni giorno alle ${n.localTime ?? '—'}`;
  if (n.repeatFreq === 'WEEKLY') {
    const days = (n.repeatDays ?? [])
      .map((d) => WEEKDAYS.find((w) => w.value === d)?.label ?? d)
      .join(', ');
    return `Ogni settimana (${days}) alle ${n.localTime ?? '—'}`;
  }
  return 'Una tantum';
}

/** Campi programmazione, condivisi da creazione e modifica. */
function ScheduleFields({ kind }: { kind: ScheduleKind }): React.ReactElement {
  return (
    <>
      <Form.Item name="kind" label="Quando inviare" rules={[{ required: true }]}>
        <Select
          options={[
            { value: 'ONESHOT', label: 'Una tantum (data e ora)' },
            { value: 'DAILY', label: 'Ogni giorno' },
            { value: 'WEEKLY', label: 'Ogni settimana (giorni scelti)' },
          ]}
        />
      </Form.Item>
      {kind === 'ONESHOT' && (
        <Form.Item
          name="when"
          label="Data e ora (Europe/Rome)"
          rules={[{ required: true, message: 'Scegli data e ora' }]}
        >
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            style={{ width: '100%' }}
            // Vieta il passato lato UI (il backend lo rifiuta comunque con 400).
            disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>
      )}
      {(kind === 'DAILY' || kind === 'WEEKLY') && (
        <Form.Item
          name="timeOfDay"
          label="Ora (Europe/Rome)"
          rules={[{ required: true, message: 'Scegli un orario' }]}
        >
          <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
        </Form.Item>
      )}
      {kind === 'WEEKLY' && (
        <Form.Item
          name="weekdays"
          label="Giorni"
          rules={[{ required: true, message: 'Scegli almeno un giorno' }]}
        >
          <Checkbox.Group options={WEEKDAYS} />
        </Form.Item>
      )}
    </>
  );
}

function ContentFields(): React.ReactElement {
  return (
    <>
      <Form.Item name="titleIt" label="Titolo (IT)" rules={[{ required: true, max: 200 }]}>
        <Input placeholder="Offerta!" />
      </Form.Item>
      <Form.Item name="bodyIt" label="Testo (IT)" rules={[{ required: true }]}>
        <Input.TextArea rows={3} placeholder="Sconto 20% su tutte le maglie." />
      </Form.Item>
      <Form.Item name="titleEn" label="Titolo (EN) — opzionale">
        <Input placeholder="Special offer!" />
      </Form.Item>
      <Form.Item name="bodyEn" label="Testo (EN) — opzionale">
        <Input.TextArea rows={2} placeholder="20% off all jerseys." />
      </Form.Item>
      <Form.Item name="categoryFilter" label="Filtro categoria (opzionale)">
        <Select
          allowClear
          placeholder="Tutti i tifosi"
          options={[
            { value: 'matches', label: 'Partite' },
            { value: 'shop', label: 'Shop' },
            { value: 'receipts', label: 'Scontrini' },
            { value: 'posts', label: 'News' },
          ]}
        />
      </Form.Item>
    </>
  );
}

export function ScheduledPushPanel(): React.ReactElement {
  const listQ = useScheduledNotifications();
  const createM = useCreateScheduled();
  const updateM = useUpdateScheduled();
  const sendNowM = useSendScheduledNow();
  const cancelM = useCancelScheduled();

  const [createForm] = Form.useForm<FormValues>();
  const [editForm] = Form.useForm<FormValues>();
  const createKind = (Form.useWatch('kind', createForm) ?? 'ONESHOT') as ScheduleKind;
  const editKind = (Form.useWatch('kind', editForm) ?? 'ONESHOT') as ScheduleKind;
  const [editing, setEditing] = React.useState<ScheduledNotification | null>(null);

  const onCreate = async (): Promise<void> => {
    const v = await createForm.validateFields().catch(() => null);
    if (!v) return;
    try {
      await createM.mutateAsync(toInput(v));
      message.success('Notifica programmata');
      createForm.resetFields();
    } catch (e) {
      message.error(apiError(e));
    }
  };

  const openEdit = (row: ScheduledNotification): void => {
    setEditing(row);
    editForm.setFieldsValue({
      titleIt: pickIt(row.title),
      titleEn: row.title?.en,
      bodyIt: pickIt(row.body),
      bodyEn: row.body?.en,
      categoryFilter: row.categoryFilter ?? undefined,
      kind: (row.repeatFreq ?? 'ONESHOT') as ScheduleKind,
      when: row.repeatFreq === null && row.nextRunAt ? dayjs(row.nextRunAt) : undefined,
      timeOfDay: row.localTime ? dayjs(row.localTime, 'HH:mm') : undefined,
      weekdays: row.repeatDays ?? undefined,
    });
  };

  const onEditSave = async (): Promise<void> => {
    if (!editing) return;
    const v = await editForm.validateFields().catch(() => null);
    if (!v) return;
    try {
      await updateM.mutateAsync({ id: editing.id, input: toInput(v) });
      message.success('Notifica aggiornata');
      setEditing(null);
    } catch (e) {
      message.error(apiError(e));
    }
  };

  const columns: ColumnsType<ScheduledNotification> = [
    {
      title: 'Messaggio',
      key: 'msg',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{pickIt(r.title)}</Typography.Text>
          <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
            {pickIt(r.body)}
          </Typography.Text>
        </Space>
      ),
    },
    { title: 'Programmazione', key: 'sched', render: (_, r) => describeSchedule(r) },
    {
      title: 'Prossima',
      dataIndex: 'nextRunAt',
      key: 'next',
      render: (v: string | null) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      key: 'status',
      render: (s: ScheduledNotification['status']) => (
        <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].label}</Tag>
      ),
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, r) => {
        const editable = r.status === 'SCHEDULED';
        const sendable = r.status === 'SCHEDULED' || r.status === 'SENDING';
        return (
          <Space wrap>
            <Popconfirm
              title="Inviare ora?"
              description={
                r.repeatFreq
                  ? 'Invio extra: la programmazione ricorrente resta attiva.'
                  : 'La notifica verrà inviata e segnata come inviata.'
              }
              okText="Invia"
              cancelText="Annulla"
              disabled={!sendable}
              onConfirm={async () => {
                try {
                  await sendNowM.mutateAsync(r.id);
                  message.success('Invio messo in coda');
                } catch (e) {
                  message.error(apiError(e));
                }
              }}
            >
              <Button size="small" icon={<SendOutlined />} disabled={!sendable}>
                Invia ora
              </Button>
            </Popconfirm>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!editable}
              onClick={() => openEdit(r)}
            >
              Modifica
            </Button>
            <Button
              size="small"
              icon={<FieldTimeOutlined />}
              disabled={!editable}
              onClick={() => openEdit(r)}
            >
              Ripianifica
            </Button>
            <Popconfirm
              title="Eliminare la notifica programmata?"
              okText="Elimina"
              okButtonProps={{ danger: true }}
              cancelText="Annulla"
              disabled={r.status === 'SENT'}
              onConfirm={async () => {
                try {
                  await cancelM.mutateAsync(r.id);
                  message.success('Notifica eliminata');
                } catch (e) {
                  message.error(apiError(e));
                }
              }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} disabled={r.status === 'SENT'}>
                Elimina
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={24}>
      <Card title="Nuova notifica programmata">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Gli orari sono nel fuso Europe/Rome."
          description="Una tantum parte alla data/ora scelta; le ricorrenti restano attive finché non le elimini."
        />
        <Form form={createForm} layout="vertical" initialValues={{ kind: 'ONESHOT' }}>
          <ContentFields />
          <ScheduleFields kind={createKind} />
          <Button
            type="primary"
            icon={<FieldTimeOutlined />}
            loading={createM.isPending}
            onClick={onCreate}
          >
            Programma
          </Button>
        </Form>
      </Card>

      <Card title="Notifiche pianificate">
        <Table<ScheduledNotification>
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          columns={columns}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: 'Nessuna notifica programmata' }}
        />
      </Card>

      <Modal
        open={editing !== null}
        title="Modifica / Ripianifica"
        onCancel={() => setEditing(null)}
        onOk={onEditSave}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={updateM.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <ContentFields />
          <ScheduleFields kind={editKind} />
        </Form>
      </Modal>
    </Space>
  );
}

function apiError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } } };
  const msg = err.response?.data?.message;
  return Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di rete');
}
